import { useMemo, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, History } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { VariantProps } from 'class-variance-authority'
import { badgeVariants } from '@/components/ui/badge'
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table'
import type { RoutineExecution } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { formatTimestamp } from './rotinasFormatters'
import { PanelEmptyState } from '@/frontend/components/EmptyState'

const PER_PAGE = 10

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ok: 'success',
  warning: 'warning',
  error: 'critical',
}

// Anatomia de th do DS (Iteração 1): 11/650 uppercase baixa.
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground'

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${mins}m`
}

function formatCost(cost: number | null): string {
  if (cost == null) return '—'
  return `R$ ${cost.toFixed(2).replace('.', ',')}`
}

interface Props {
  // Execuções REAIS (fleet_sessions channel=cron), não mais fabricadas.
  executions: RoutineExecution[]
}

/**
 * Lista "Últimas execuções" da aba Rotinas (Iteração 3, Onda C): agora com
 * execuções REAIS — quando (timestamp real da sessão), duração, custo e status
 * (só quando derivável). Substitui a projeção fabricada
 * (rotinasExecutionProjection). Linhas com output podem abrir a sessão.
 */
export default function RotinasJobList({ executions }: Props) {
  const [page, setPage] = useState(0)
  const entries = useMemo(() => executions, [executions])

  useEffect(() => setPage(0), [entries])

  const totalPages = Math.ceil(entries.length / PER_PAGE)
  const pageEntries = entries.slice(page * PER_PAGE, (page + 1) * PER_PAGE)

  if (entries.length === 0) {
    return <PanelEmptyState icon={History} title="Nenhuma execução ainda" description="Quando a rotina rodar, aparece aqui." framed={false} />
  }

  return (
    <div>
      <Table className="text-[13px]">
        <TableHeader>
          <TableRow>
            <TableHead className={TH_CLS}>Rotina</TableHead>
            <TableHead className={TH_CLS}>Quando</TableHead>
            <TableHead className={TH_CLS}>Duração</TableHead>
            <TableHead className={TH_CLS}>Custo</TableHead>
            <TableHead className={`text-right ${TH_CLS}`}>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageEntries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>
                <span className="font-medium text-foreground">{entry.routine_name}</span>
                {entry.from_cron_state && (
                  <span className="ml-1.5 text-[11px] text-faint-foreground">· última run</span>
                )}
              </TableCell>
              <TableCell>
                <span className="font-mono text-[11.5px] text-muted-foreground">
                  {entry.last_event_at ? formatTimestamp(Date.parse(entry.last_event_at)) : '—'}
                </span>
              </TableCell>
              <TableCell>
                <span className="font-mono text-[11.5px] text-muted-foreground">{formatDuration(entry.duration_seconds)}</span>
              </TableCell>
              <TableCell>
                <span className="font-mono text-[11.5px] text-muted-foreground">{formatCost(entry.total_cost)}</span>
              </TableCell>
              <TableCell className="text-right">
                {entry.status ? (
                  <Badge variant={STATUS_VARIANT[entry.status.kind] ?? 'info'}>{entry.status.label}</Badge>
                ) : (
                  <span className="text-[12px] text-faint-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <span className="text-[13px] tabular-nums text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              aria-label="Página anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              aria-label="Próxima página"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
